import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight } from "lucide-react";
import { JournalPost } from "@/lib/journal";
import { useLanguage } from "@/contexts/LanguageContext";

interface JournalCardProps {
  post: JournalPost;
}

const JournalCard: React.FC<JournalCardProps> = ({ post }) => {
  const { language } = useLanguage();
  const { title, description, date, image, category } = post.meta;

  const formattedDate = new Date(date).toLocaleDateString(
    language === "de" ? "de-CH" : language === "fr" ? "fr-CH" : "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );

  const postLink = language === "en" 
    ? `/journal/${post.slug}` 
    : `/${language}/journal/${post.slug}`;

  return (
    <div className="group flex flex-col h-full bg-transparent">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-sm mb-6 shadow-sm">
        <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors z-10" />
        <img 
          src={image} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 z-20">
          <Badge className="bg-white/95 text-gray-900 hover:bg-white border-none shadow-sm uppercase text-xs tracking-wider rounded-sm px-2 py-1">
            {category}
          </Badge>
        </div>
      </div>
      
      <div className="flex flex-col flex-grow">
        <div className="flex items-center text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">
          <span className="text-alpine-gold mr-2">•</span>
          <span>{formattedDate}</span>
        </div>
        
        <h3 className="text-xl font-playfair font-bold leading-tight text-gray-900 mb-3 group-hover:text-alpine-gold transition-colors">
          <Link to={postLink}>
            {title}
          </Link>
        </h3>
        
        <p className="text-gray-600 line-clamp-3 text-sm leading-relaxed mb-4 font-light">
          {description}
        </p>
        
        <div className="mt-auto pt-2">
          <Link 
            to={postLink} 
            className="inline-flex items-center text-xs font-bold uppercase tracking-widest text-gray-900 hover:text-alpine-gold transition-colors border-b border-gray-200 pb-1 hover:border-alpine-gold"
          >
            {language === "de" ? "Lesen" : language === "fr" ? "Lire" : "Read Story"}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default JournalCard;
